import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import {
  TmoFieldset,
  TmoButtons,
  TmoTextarea
} from '@tmobile/tmo-bps-syncup-web-component-lib';

// Higher-order component and services
import withModal from '../../../modal/withModal';
import iotFirmwareService from '../../../../services/iotFirmwareService';

// Context providers
import StatusContext from '../../../../context/StatusContext';
import SyncUpProductContext from '../../../../context/SyncUpProductContext';

// Custom hooks and utilities
import useVisible from '../../../../hooks/useVisible';
import { trackCustomEvents } from '../../../../utils/appInsight_analytics';

// UI components and assets
import TmoSvg from '../../../uiHelpers/TmoSvg';
import style from '../Groups.module.css';
import update from '../../../../static/assets/icons/check.svg';

/**
 * AddDevices Component
 * ===================
 * Provides bulk device addition functionality to firmware groups with
 * multi-format input parsing, validation, and user feedback.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.group - Target firmware group information
 * @param {string} props.group.id - Unique group identifier for API calls
 * @param {string} props.group.name - Human-readable group name for user feedback
 * @param {Function} props.onAdd - Callback function to refresh parent data after addition
 */
function AddDevices({ group, onAdd }) {
  /**
   * Local state management
   * ======================
   * Manages user input and component lifecycle state for
   * optimal user experience and operation control.
   */
  const [input, setInput] = useState(); // Raw user input for device IDs

  /**
   * Context dependencies
   * ===================
   * Access to application-wide contexts for notifications,
   * product information, and multi-tenant support.
   */
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);
  const syncUpProductContext = useContext(SyncUpProductContext);

  /**
   * Loading state management
   * =======================
   * Custom hook for managing visibility/loading states with
   * optimized boolean state handling.
   */
  const { visible, setVisible } = useVisible(false);

  /**
   * Group destructuring for cleaner code
   * ====================================
   * Extract frequently used group properties to improve
   * readability and reduce object property access chains.
   */
  const { name, id } = group;
  const onAddDevices = async () => {
    if (input) {
      const hardware = input
        ?.split(/\n/g) // Split on newlines
        .join(',') // Join with commas for normalization
        .split(','); // Split on commas for final array
      trackCustomEvents(`Add Devices`, {
        groupId: id,
        hardwareIds: hardware
      });

      try {
        setVisible(true);
        await iotFirmwareService.addDevicesToFirmwareGroup({
          groupId: id,
          body: {
            hardwareIds: hardware // Parsed and normalized device ID array
          },
          product: syncUpProductContext.syncUpAppName // Multi-tenant context
        });
        await onAdd();
        setVisible(false);
        addSuccessMessage({
          message: `Successfully added devices to group: ${name}`
        });
      } catch (error) {
        setVisible(false);
        addErrorMessage({ error });
      }
    }
  };

  return (
    <>
      {!visible ? (
        /**
         * Device input form
         * ================
         * Provides intuitive interface for bulk device ID entry
         * with flexible format support and clear user guidance.
         */
        <form>
          <TmoFieldset className={style.container}>
            {/* Input section with label and guidance */}
            <div className={style.form_container}>
              <label className={style.label}>Hardware Id's</label>
              <TmoTextarea
                onChange={setInput} // Real-time input capture
                className={style.textarea} // Consistent styling
                rows="5" // Adequate space for multiple IDs
                name="hardwareIds" // Form field identification
                value={input} // Controlled input value
                placeholder="Comma separated device ID's" // User guidance for format
              />
            </div>

            {/* Action section with validation */}
            <div className={style.add_device_action}>
              <TmoButtons
                type="magenta_primary" // T-Mobile primary button styling
                onClick={onAddDevices} // Trigger addition workflow
                disabled={!input} // Prevent submission without input
                icon={<ReactSVG src={update} className={style.icon} />} // Visual action indicator
              >
                Add Devices
              </TmoButtons>
            </div>
          </TmoFieldset>
        </form>
      ) : (
        <TmoSvg
          className={classNames(
            style.spinner, // Base spinner styles
            visible && style.spinner_deleting // Active state modifier
          )}
          svgId="loading-spinner" // Specific spinner variant
        />
      )}
    </>
  );
}

export default withModal(AddDevices);
